import { QuestionCircleOutlined } from "@ant-design/icons";
import { AssetsHost, COLORS } from "@coeff/utils";
import React from "react";
import styled from "styled-components";

import { Card, CardContentIndent, CardHeader } from "../../common/Card";
import * as Graphics from "../../common/Icons/graphics";
import {
  CardIconDate,
  CardIconDimension,
  CardIconMetric,
  Columns,
  ColumnsIconCompact,
  CurvedNoArrow,
  Filter,
  ImportPreviewObject,
  Info,
  NewWindow,
  Sort,
  Source,
  SourceAccount,
  SourceFile,
  SQL,
} from "../../common/Icons/icons";
import { ImportTypeIcon } from "../../common/ImportTypeIcon";
import { Tooltip } from "../../common/Tooltip";

import { AdvanceSettingPopover } from "./AdvanceSettingPopover";
import { AuthGraphics } from "./AuthGraphics";
import { AuthorizeSQL } from "./AuthorizeSQL";
import { AuthorizeTableau } from "./AuthorizeTableau";
import { APIKeyAuthButton, Button, OAuth2Button, OpenUrlButton, SetParamButton } from "./Button";
import { Checkbox } from "./Checkbox";
import { ConfirmBox } from "./ConfirmBox";
import { DatePicker } from "./DatePicker";
import { ErrorCard } from "./ErrorCard";
import { TextArea, TextBox } from "./Input";
import { Link } from "./Link";
import { Popover } from "./Popover";
import { RadioGroup } from "./RadioGroup";
import { ReAuthInfo } from "./ReAuthInfo";
import { SalesforceAppSetup } from "./SalesforceAppSetup";
import { Select } from "./Select";
import { SourceInfoCard } from "./SourceInfoCard";
import { Switch } from "./Switch";

// Styled components for PPress forms
interface LabelProps {
  inline?: boolean;
  textAlign?: string;
  color?: string;
  fontWeight?: string;
}

const Label = styled.label<LabelProps>`
  font-weight: bold;
  margin-top: 8px;
  display: ${props => (props.inline ? "inline" : "block")};
  text-align: ${props => props.textAlign || "inherit"};
  ${props => (props.color ? `color: ${props.color};` : "")}
  ${props => (props.fontWeight ? `font-weight: ${props.fontWeight};` : "")}
`;

const HelpText = styled.div`
  font-size: 12px;
  margin: 4px auto;
  color: ${COLORS.black45};
`;

interface SpaceProps {
  height?: string;
}
const Spacer = styled.div<SpaceProps>`
  height: ${props => props.height || "1rem"};
`;

const Image: React.FC<{ src: string }> = props => (
  <img {...props} src={`${AssetsHost()}/${props.src}`} />
);

// Auth-only PPress namespace - minimal set of components needed for auth flows
export const PPressAuthNamespace = {
  // Auth-specific components
  SalesforceAppSetup,
  AdvanceSettingPopover,
  APIKeyAuthButton,
  AuthGraphics,
  AuthorizeSQL,
  AuthorizeTableau,
  Button,
  Card,
  CardHeader,
  CardContentIndent,
  Checkbox,
  ConfirmBox,
  DatePicker,
  ErrorCard,
  // Graphics imported from the shared library
  Graphics: {
    ActiveCampaignExampleGraphic: Graphics.ActiveCampaignExampleGraphic,
    BasecampGraphic: Graphics.BasecampGraphic,
    BigCommerceGraphic: Graphics.BigCommerceGraphic,
    ChargeBeeSiteExampleGraphic: Graphics.ChargeBeeSiteExampleGraphic,
    EgnyteDomainExampleGraphics: Graphics.EgnyteDomainExampleGraphics,
    FreshdeskExampleGraphic: Graphics.FreshdeskExampleGraphic,
    GongExampleGraphic: Graphics.GongExampleGraphic,
    HubspotHelpStep1Graphic: Graphics.HubspotHelpStep1Graphic,
    HubspotHelpStep2Graphic: Graphics.HubspotHelpStep2Graphic,
    LookerUrlExampleGraphic: Graphics.LookerUrlExampleGraphic,
    NetSuiteAccountIDHelpGraphic: Graphics.NetSuiteAccountIDHelpGraphic,
    ShopifyAccountExampleGraphic: Graphics.ShopifyAccountExampleGraphic,
    WordPressGraphic: Graphics.WordPressGraphic,
  },
  HelpText,
  // Icons and CardIcons collections
  CardIcons: {
    Columns,
    ColumnsIconCompact,
    CurvedNoArrow,
    Filter,
    Source,
    SourceAccount,
    SourceFile,
    SQL,
    CardIconMetric,
    CardIconDimension,
    CardIconDate,
  },
  Icons: {
    ImportPreviewObject,
    Info,
    NewWindow,
    Sort,
    QuestionCircleOutlined,
  },
  Image,
  ImportTypeIcon,
  Label,
  Link,
  OAuth2Button,
  OpenUrlButton,
  Popover,
  RadioGroup,
  ReAuthInfo,
  Select,
  SetParamButton,
  SourceInfoCard,
  Spacer,
  Switch,
  TextArea,
  TextBox,
  Tooltip,
};

export const PPress = PPressAuthNamespace;
